import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import TypographyOverride from "./TypographyOverride";

export default function Navbar({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const name = settings?.agency_name || "Artistry Perspectives";
  const tagline = settings?.tagline;
  const logo = settings?.logo_url;

  // Render the wordmark with a subtle gold accent dot between words
  const renderWordmark = () => {
    const parts = (name || "Artistry Perspectives").split(" ");
    if (parts.length < 2) return <span className="text-white">{name}</span>;
    const first = parts[0];
    const rest = parts.slice(1).join(" ");
    return (
      <>
        <span className="text-white">{first}</span>
        <span className="text-[#C9A050] mx-1.5 sm:mx-2">·</span>
        <span className="text-white">{rest}</span>
      </>
    );
  };

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0C]/60 border-b border-white/5"
          : "bg-transparent"
      }`}
      style={{
        // Extend the nav's tinted background upward INTO the iOS safe area
        // (behind the notch / Dynamic Island / status bar) so there's no
        // visible black gap between the OS status bar and the tinted nav
        // on iPhone. Content is pushed below the safe area by the same
        // padding, so the logo and hamburger never render under the notch.
        // Zero effect on desktop and non-notch Android — env() returns 0px.
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* Nav-specific typography overrides — Typography → Navigation Bar admin.
         Use it to tune the logo wordmark + link sizing on Android where
         font rendering differs from iOS. */}
      <TypographyOverride pageId="nav" scopeSelector='[data-testid="main-nav"]' prefix="nav" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 min-h-20 py-3 md:py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 sm:gap-4 group min-w-0"
        >
          {logo ? (
            <img
              src={logo}
              alt={name}
              className="h-9 md:h-10 w-auto object-contain shrink-0"
            />
          ) : null}
          <span className="flex flex-col leading-tight min-w-0">
            <span
              data-testid="nav-agency-name"
              className="font-display text-[15px] sm:text-[17px] md:text-[20px] tracking-[0.22em] uppercase whitespace-nowrap"
            >
              {renderWordmark()}
            </span>
            {tagline && (
              <span
                data-testid="nav-tagline"
                className="font-serif-accent text-[13px] sm:text-[14px] md:text-[16px] text-[#8A8A93] mt-1 truncate max-w-[60vw] sm:max-w-none"
              >
                {tagline}
              </span>
            )}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10 shrink-0">
          {[
            { to: "/", label: "Home" },
            { to: "/work", label: "Work" },
            { to: "/case-studies", label: "Case Study" },
            { to: "/marketing", label: "Our Approach" },
            { to: "/blog", label: "Blog" },
            ...(settings?.show_ai_tab ? [{ to: "/ai", label: "AI" }] : []),
            { to: "/about", label: "About" },
            { to: "/contact", label: "Contact" },
          ].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `overline hover:text-white transition-colors ${
                  isActive ? "text-white" : "text-[#8A8A93]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={`https://wa.me/${(settings?.whatsapp_number || "").replace(/\D/g, "")}?text=${encodeURIComponent(settings?.whatsapp_message || "Hello")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-cta"
            className="overline text-white border border-white/20 px-5 py-2.5 hover:border-[#C9A050] hover:text-[#C9A050] transition-all"
          >
            Start a project
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white p-2 shrink-0"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          <div className="flex flex-col gap-1.5">
            <span className={`block w-6 h-px bg-white transition-all ${open ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`block w-6 h-px bg-white transition-all ${open ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-px bg-white transition-all ${open ? "-rotate-45 -translate-y-2" : ""}`} />
          </div>
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0A0A0C]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-10 flex flex-col gap-6">
            <NavLink to="/" onClick={() => setOpen(false)} className="font-display text-3xl">Home</NavLink>
            <NavLink to="/work" onClick={() => setOpen(false)} className="font-display text-3xl">Work</NavLink>
            <NavLink to="/case-studies" onClick={() => setOpen(false)} className="font-display text-3xl">Case Study</NavLink>
            <NavLink to="/marketing" onClick={() => setOpen(false)} className="font-display text-3xl">Our Approach</NavLink>
            <NavLink to="/blog" onClick={() => setOpen(false)} className="font-display text-3xl">Blog</NavLink>
            {settings?.show_ai_tab && (
              <NavLink to="/ai" onClick={() => setOpen(false)} className="font-display text-3xl">AI</NavLink>
            )}
            <NavLink to="/about" onClick={() => setOpen(false)} className="font-display text-3xl">About</NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)} className="font-display text-3xl">Contact</NavLink>
            <a
              href={`https://wa.me/${(settings?.whatsapp_number || "").replace(/\D/g, "")}?text=${encodeURIComponent(settings?.whatsapp_message || "Hello")}`}
              target="_blank"
              rel="noreferrer"
              className="overline text-[#C9A050] mt-4"
            >
              Start a project →
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
